import { memo, useMemo } from 'react';
import styles from './DataTable.module.css';
import { Sparkline } from './Sparkline';
import { StatusBadge, type PslLevel } from './StatusBadge';
import { InfoTip } from './ui/InfoTip';
import { FIELD_HELP } from '@/lib/fieldHelp';
import actionsIcon from '@/assets/icons/actions-dots.svg';
import pillShare from '@/assets/icons/pill-share.svg';
import pillCount from '@/assets/icons/pill-count.svg';
import pillSize from '@/assets/icons/pill-size.svg';

export type ColumnKind = 'label' | 'psl' | 'metric' | 'value' | 'actions';
export type Align = 'left' | 'center' | 'right';

export interface ColumnDef {
  key: string;
  header: string;
  /** Exact px width from Figma — column sums must equal 1124. */
  width: number;
  kind: ColumnKind;
  /** Column headers carry a trailing info affordance except the label column. */
  info?: boolean;
  /** Key into FIELD_HELP for the hover definition. */
  help?: string;
  headerAlign?: Align;
  /** Figma uses two sparkline heights: Group 20 (18.364) and Group 19 (15.602). */
  sparkHeight?: number;
  /** Group 20 strokes #909090; Group 19 strokes #b7b7b7. */
  sparkStroke?: string;
}

export type PillIcon = 'share' | 'count' | 'size';
export interface Pill { icon: PillIcon; text: string }

export interface MetricValue {
  value: string;
  delta?: string;
  direction?: 'up' | 'down';
  series?: number[];
  /** Design-space x positions; omitted for live data, which spaces evenly. */
  seriesX?: number[];
}

export interface TableRow {
  id: string;
  /** What the user reads. May be presented differently from `value`. */
  label: string;
  /**
   * The raw warehouse value this row filters by. Cities come back lower-cased
   * and are displayed capitalised, so filtering on the label would ask the API
   * for a city that does not exist. Defaults to `label` when they are the same.
   */
  value?: string;
  /** Stable identity (product_id for SKUs) used by drill-down overlays. */
  entityId?: string;
  image?: string;
  pills?: Pill[];
  /** Gap between pills: 8px in Category/City, 10px in the SKU table. */
  pillGap?: number;
  psl?: PslLevel;
  cells: Record<string, MetricValue>;
  selected?: boolean;
}

const PILL_ICON: Record<PillIcon, string> = { share: pillShare, count: pillCount, size: pillSize };

const HEADER_ALIGN: Record<Align, string> = {
  left: styles.headerAlignLeft,
  center: styles.headerAlignCenter,
  right: styles.headerAlignRight,
};

export interface DataTableProps {
  columns: ColumnDef[];
  rows: TableRow[];
  caption: string;
  /**
   * Render the frame with no values in it.
   *
   * The alternative the page used to take was to fall back to the Figma
   * fixture until live data arrived, which put fabricated numbers on screen
   * that were indistinguishable from real ones. A number nobody measured is
   * worse than no number at all, so loading shows structure and nothing else.
   */
  loading?: boolean;
  /** Placeholder rows to draw while loading. Matches the page's row cap. */
  skeletonRows?: number;
  onRowClick?: (row: TableRow) => void;
  onActionsClick?: (row: TableRow) => void;
}

function DataTableImpl({
  columns, rows, caption, loading = false, skeletonRows = 7, onRowClick, onActionsClick,
}: DataTableProps) {
  const template = useMemo(() => columns.map((c) => `${c.width}px`).join(' '), [columns]);

  return (
    <section className={styles.table} aria-label={caption}>
      <div className={styles.headerRow} style={{ gridTemplateColumns: template }} role="row">
        {columns.map((col) => (
          <div
            key={col.key}
            role="columnheader"
            className={`${styles.headerCell} ${HEADER_ALIGN[col.headerAlign ?? (col.kind === 'label' ? 'left' : 'center')]}`}
          >
            <span className={styles.headerLabel}>
              {col.header}
              {col.info && (
                <InfoTip
                  text={FIELD_HELP[col.help ?? col.key]?.text ?? col.header}
                  {...(FIELD_HELP[col.help ?? col.key]?.formula
                    ? { formula: FIELD_HELP[col.help ?? col.key].formula }
                    : {})}
                />
              )}
            </span>
          </div>
        ))}
      </div>

      {loading
        ? Array.from({ length: skeletonRows }, (_, i) => (
            <div
              key={`skeleton-${i}`}
              role="row"
              aria-hidden="true"
              className={styles.row}
              style={{ gridTemplateColumns: template }}
            >
              {columns.map((col) => (
                <SkeletonCell key={col.key} col={col} />
              ))}
            </div>
          ))
        : rows.map((row) => (
            <div
              key={row.id}
              role="row"
              className={`${styles.row} ${row.selected ? styles.selected : ''}`}
              style={{ gridTemplateColumns: template }}
              onClick={() => onRowClick?.(row)}
            >
              {columns.map((col) => (
                <Cell key={col.key} col={col} row={row} onActionsClick={onActionsClick} />
              ))}
            </div>
          ))}
    </section>
  );
}

/**
 * A cell-shaped shimmer. Widths vary per column kind so the skeleton reads as
 * the table it is standing in for rather than a row of identical grey bars —
 * the layout must not jump when the real values land.
 */
function SkeletonCell({ col }: { col: ColumnDef }) {
  if (col.kind === 'actions') {
    return <div className={`${styles.cell} ${styles.skeletonCell}`} role="cell" />;
  }
  if (col.kind === 'label') {
    return (
      <div className={`${styles.cell} ${styles.labelCell} ${styles.skeletonCell}`} role="cell">
        <div className={styles.labelStack}>
          <span className={`${styles.shimmer} ${styles.shimmerLabel}`} />
          <span className={`${styles.shimmer} ${styles.shimmerPill}`} />
        </div>
      </div>
    );
  }
  if (col.kind === 'psl') {
    return (
      <div className={`${styles.cell} ${styles.pslCell} ${styles.skeletonCell}`} role="cell">
        <span className={`${styles.shimmer} ${styles.shimmerBadge}`} />
      </div>
    );
  }
  if (col.kind === 'metric') {
    return (
      <div className={`${styles.cell} ${styles.metricCell} ${styles.skeletonCell}`} role="cell">
        <span className={`${styles.shimmer} ${styles.shimmerValue}`} />
        <span className={`${styles.shimmer} ${styles.shimmerSpark}`} />
      </div>
    );
  }
  return (
    <div className={`${styles.cell} ${styles.valueCell} ${styles.skeletonCell}`} role="cell">
      <span className={`${styles.shimmer} ${styles.shimmerValue}`} />
    </div>
  );
}

function Cell({
  col,
  row,
  onActionsClick,
}: {
  col: ColumnDef;
  row: TableRow;
  onActionsClick?: (row: TableRow) => void;
}) {
  if (col.kind === 'label') {
    return (
      <div className={`${styles.cell} ${styles.labelCell}`} role="cell">
        {row.image && <img className={styles.thumb} src={row.image} alt="" />}
        <div className={styles.labelStack}>
          {/* Truncated names are unreadable without this — the full SKU is
              often the only thing distinguishing two rows. */}
          <span className={styles.labelText} title={row.label}>{row.label}</span>
          {row.pills && row.pills.length > 0 && (
            <div className={styles.pills} style={{ gap: `${row.pillGap ?? 8}px` }}>
              {row.pills.map((pill) => (
                <span className={styles.pill} key={`${pill.icon}:${pill.text}`}>
                  <img className={styles.pillIcon} src={PILL_ICON[pill.icon]} alt="" />
                  {pill.text}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (col.kind === 'psl') {
    return (
      <div className={`${styles.cell} ${styles.pslCell}`} role="cell">
        {row.psl && <StatusBadge level={row.psl} />}
      </div>
    );
  }

  if (col.kind === 'actions') {
    return (
      <div className={`${styles.cell} ${styles.actionsCell}`} role="cell">
        <button
          type="button"
          className={styles.actionsBtn}
          aria-label={`Actions for ${row.label}`}
          onClick={(e) => {
            e.stopPropagation();
            onActionsClick?.(row);
          }}
        >
          <img className={styles.actionsIcon} src={actionsIcon} alt="" />
        </button>
      </div>
    );
  }

  const metric = row.cells[col.key];
  if (!metric) return <div className={styles.cell} role="cell" />;

  const isMetric = col.kind === 'metric';

  return (
    <div className={`${styles.cell} ${isMetric ? styles.metricCell : styles.valueCell}`} role="cell">
      <span className={styles.valueLine}>
        <span className={styles.value}>{metric.value}</span>
        {metric.delta && (
          <span className={`${styles.delta} ${metric.direction === 'down' ? styles.deltaDown : styles.deltaUp}`}>
            {metric.delta}
          </span>
        )}
      </span>
      {isMetric && metric.series && (
        <Sparkline
          points={metric.series}
          xs={metric.seriesX}
          width={140}
          height={col.sparkHeight ?? 18.3634}
          stroke={col.sparkStroke}
        />
      )}
    </div>
  );
}

export const DataTable = memo(DataTableImpl);
